import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useInView } from 'react-intersection-observer';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { CommercialMessage, VoucherModal } from '@pegasus/commercial-messages';
import { usePlatformJourneys } from '@pegasus/platform-journeys';
import { useConfig } from '@browsing/config';
import { tracker, trackSelectPromotion } from '@browsing/tracking';

import { Spinner } from '@client/components/shared/Shared';
import SecondarySubscriptionContainer from '@client/components/shared/SecondarySubscription/SecondarySubscription';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { scrollTo } from '@client/helpers';
import { getElementRealHeight } from '@client/helpers/dom';
import { isSmScreen } from '@client/helpers/screens';
import { track } from '@client/helpers/tracking';
import { trackEvents, M1_REBRANDING } from '@client/components/mylounge/constants';
import { useIsPlatformJourney } from '@client/components/mylounge/platform-journeys';
import { THB, ThbLinkType } from '@shared/types/mylounge';
import { getTabs, getImagesLoaded, getCategoryTabId } from '@client/redux/modules/mylounge';
import { useVariant } from '@client/lib/assignments';
import { TabsWrapper as Tabs } from './Tabs/TabsWrapper';
import { findActiveTab, getTabImagesMap, buildUrlForThb } from './helpers';
import BannerSlider from './banner-slider';

import {
    CATEGORY_TAB_ID_TRACK_MAPPING,
    SCROLL_TRACK_TOP_OFFSET,
    TAB_SWIPE_TRACK_EVENT_NAME,
    SCROLL_TRACK_EVENT_NAME,
} from './constants';
import {
    OuterWrapper,
    Wrapper,
    TabsWrapper,
    SpinnerWrapper,
    AlertBannerWrapper,
    CommercialWrapper,
} from './Banner.styles';

import useCustomCommercialMessage from './use-commercial-message';
import { ApplicationContext } from '../helpers';

interface Props {
    wrapperId: string;
}

export const Banner = ({ wrapperId }: Props) => {
    const history = useHistory();
    const config = useConfig();
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);
    // Commercial Messages hooks.
    const commercialMessage = useCustomCommercialMessage();

    // Platform Journey hooks.
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const { onboardingType, isEligibleForVoucher } = usePlatformJourneys();
    const isAutoLoginActive = onboardingType === 'FASHION_STORE' && isEligibleForVoucher;

    // selectors
    const clientWidth = useSelector(getClientDeviceWidth);
    const tabs = useSelector(getTabs);
    const imagesLoaded = useSelector(getImagesLoaded);

    const tabsWrapperRef = useRef<HTMLDivElement>(null);

    // state
    const tabChangeTimeout = useRef<ReturnType<typeof setTimeout>>(null);
    const [isTabsVisible, setIsTabsVisible] = useState(false);
    const [scrollTracked, setScrollTracked] = useState(false);
    const categoryTabId = useSelector(getCategoryTabId);
    const [activeIndex, setActiveIndex] = useState(findActiveTab(tabs, categoryTabId));
    const [lastIndex, setLastIndex] = useState(null);

    const { smartbannerVisibility } = useContext(ApplicationContext);

    // Experiments

    const isThbResizeHeightVariant = useVariant(
        '673eef14-9017-4a5f-bd7e-501f3c94f5f9',
        'variant_thb_sizeDecrease',
    );

    const isThbResizeWidthVariant = useVariant('thb_max_width', 'variant_thb_width');

    // load only active banner image
    const [tabImages, setTabImages] = useState(getTabImagesMap(tabs, activeIndex, false));

    const { thb } = tabs?.[activeIndex] || { thb: {} as THB };

    const trackTabEvent = useCallback(
        (eventName: string, index: number) => {
            const { id } = tabs[index];

            track(eventName, {
                campaign_level_2: (CATEGORY_TAB_ID_TRACK_MAPPING as any)[id] || id,
            });
        },
        [tabs],
    );

    const trackScroll = useCallback(() => {
        if (
            !scrollTracked &&
            (!isSmScreen(clientWidth) || window.scrollY > SCROLL_TRACK_TOP_OFFSET)
        ) {
            setScrollTracked(true);
            trackTabEvent(SCROLL_TRACK_EVENT_NAME, activeIndex);
        }
    }, [scrollTracked, clientWidth, trackTabEvent, activeIndex]);

    const [outerWrapperRef, isOuterWrapperIntersected] = useInView({
        threshold: 0,
        initialInView: true,
    });

    useScrollPosition(
        ({ prevPos, currPos }) => {
            trackScroll();

            const isSmOrLess = isSmScreen(clientWidth);
            const scrollDown = currPos.y > prevPos.y;

            setIsTabsVisible(!isSmOrLess || !scrollDown);
        },
        [trackScroll, clientWidth],
        null,
        true,
        100,
    );

    // commponent unmount
    useEffect(
        () => () => {
            if (tabChangeTimeout.current) {
                clearTimeout(tabChangeTimeout.current);
                tabChangeTimeout.current = null;
            }
        },
        [],
    );

    // changing tab
    useEffect(() => {
        const newActiveIndex = findActiveTab(tabs, categoryTabId);
        if (newActiveIndex !== activeIndex) {
            setActiveIndex(newActiveIndex);
            setLastIndex(activeIndex);
            setTabImages(currentTabImages => ({
                ...currentTabImages,
                [newActiveIndex]: true,
            }));
            setScrollTracked(false);
        }
    }, [activeIndex, categoryTabId, tabs]);

    // unlock loading all banner images
    // TODO: move this logic inside the BannerSlider
    useEffect(() => {
        if (imagesLoaded) {
            setTabImages(getTabImagesMap(tabs, activeIndex, true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagesLoaded]);

    const trackBannerClick = useCallback(() => {
        track('co-discover-button', {
            brandClicked: thb.id,
            campaign_level_2: categoryTabId,
        });

        if (config.isEnabled('ga4')) {
            trackSelectPromotion({
                element: 'thb',
                creativeName: 'thb',
                itemListName: 'thb',
                item: {
                    campaignIdentifier: thb.id,
                    categoryId: categoryTabId,
                    index: activeIndex,
                },
            });
        }
    }, [thb.id]);

    const navigateToThbTarget = useCallback(() => {
        if (!thb) {
            return;
        }

        trackBannerClick();
        const url = buildUrlForThb(thb, categoryTabId);

        if (!url) {
            return;
        }

        if (thb.type === ThbLinkType.TAB) {
            history.push(url);
        } else {
            window.location.href = url;
        }
    }, [thb, trackBannerClick, categoryTabId, history]);

    const changeActiveTab = useCallback(
        (newActiveIndex: number) =>
            history.push(`/event/${tabs?.[newActiveIndex]?.id}${window.location.search}`),
        [history, tabs],
    );

    const isSameTab = useCallback(
        (newActiveIndex: number) => newActiveIndex === activeIndex,
        [activeIndex],
    );

    const handleTabChange = useCallback(
        (newActiveIndex: number, trackEventName: string) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(trackEventName, newActiveIndex);

            tracker.track('GA4-EEC-select_content', {
                event_name: 'select_content',
                select_content: {
                    element: 'category_tab',
                    content_type: 'swipe',
                    content_id: categoryTabId,
                },
            });
            changeActiveTab(newActiveIndex);

            const wrapper = document.getElementById(wrapperId);

            if (!isOuterWrapperIntersected && wrapper && tabsWrapperRef.current) {
                const height = getElementRealHeight(tabsWrapperRef.current);
                const { offsetTop } = wrapper;

                const top = offsetTop - height;

                scrollTo({ top });
            }
        },
        [isSameTab, trackTabEvent, changeActiveTab, wrapperId, isOuterWrapperIntersected],
    );

    const handleTabSwipe = useCallback(
        (newActiveIndex: number) => handleTabChange(newActiveIndex, TAB_SWIPE_TRACK_EVENT_NAME),
        [handleTabChange],
    );

    if (!tabs || !tabs.length || !tabs[activeIndex]) {
        return (
            <SpinnerWrapper>
                <Spinner black={true} />
            </SpinnerWrapper>
        );
    }

    return (
        <OuterWrapper
            className={`${isThbResizeWidthVariant ? 'grid grid-page-layout' : ''}`}
            isM1Rebranding={isM1Rebranding}
            ref={outerWrapperRef}
            id="thb-wrapper"
        >
            <Wrapper isThbResizeVariant={isThbResizeHeightVariant}>
                {!isAutoLoginActive && !isPlatformJourneyEnabled && !commercialMessage.isEnabled && (
                    <AlertBannerWrapper>
                        <SecondarySubscriptionContainer />
                    </AlertBannerWrapper>
                )}

                {!isPlatformJourneyEnabled &&
                    commercialMessage.isEnabled &&
                    commercialMessage.event &&
                    !commercialMessage.isModal &&
                    !isM1Rebranding && (
                        <TransitionGroup>
                            {commercialMessage.isShown && (
                                <CSSTransition classNames="commercial-transition" timeout={300}>
                                    <CommercialWrapper
                                        floating={!isOuterWrapperIntersected}
                                        floatingHidden={
                                            !isTabsVisible && !isOuterWrapperIntersected
                                        }
                                    >
                                        <CommercialMessage
                                            event={commercialMessage.event}
                                            onRedeem={commercialMessage.onRedeem}
                                            onClose={commercialMessage.onClose}
                                            track={track}
                                            trackEvents={trackEvents}
                                        />
                                    </CommercialWrapper>
                                </CSSTransition>
                            )}
                        </TransitionGroup>
                    )}

                {!isPlatformJourneyEnabled &&
                    commercialMessage.isEnabled &&
                    commercialMessage.isModal &&
                    commercialMessage.isShown && (
                        <CommercialMessage
                            component={VoucherModal}
                            event={commercialMessage.event}
                            onClose={commercialMessage.onClose}
                            onRedeem={commercialMessage.onRedeem}
                            track={track}
                            trackEvents={trackEvents}
                        />
                    )}

                <BannerSlider
                    // @ts-ignore
                    tabs={tabs}
                    activeIndex={activeIndex}
                    lastIndex={lastIndex}
                    onBannerSwipe={handleTabSwipe}
                    tabImages={tabImages}
                    onImageClick={navigateToThbTarget}
                    isThbResizeVariant={isThbResizeWidthVariant}
                />
            </Wrapper>
            {!isM1Rebranding && (
                <TabsWrapper
                    smartbannerVisibility={!!smartbannerVisibility}
                    floating={!isOuterWrapperIntersected}
                    floatingHidden={!isTabsVisible && !isOuterWrapperIntersected}
                    ref={tabsWrapperRef}
                >
                    <Tabs bannerTabsRef={tabsWrapperRef} floating={!isOuterWrapperIntersected} />
                </TabsWrapper>
            )}
        </OuterWrapper>
    );
};

export default Banner;
